import { useState, useEffect } from 'react';
import { ReportProblemModal } from '../support/ReportProblemModal';
import { NotificationDropdown } from '../notifications/NotificationDropdown';
import { SupabaseStatus } from '../common/SupabaseStatus';
import { SignedImage } from '../common/SignedFile';
import {
  Truck,
  LayoutDashboard,
  Package,
  Users,
  Wrench,
  DollarSign,
  FileText,
  Settings,
  LogOut,
  Bell,
  Activity,
  Container,
  BarChart3,
  Search,
  HelpCircle,
  AlertOctagon,
  MapPin,
  Shield,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  Clock,
  Send,
  CheckCircle2,
  CreditCard,
  Navigation,
  Brain,
  BookOpen,
  Building2,
  Landmark,
  Briefcase,
  Fuel,
  Receipt,
  UserCheck,
  UserCog,
  Minus,
  Plus,
  FileClock,
  FileCheck,
  TrendingUp,
  FileSpreadsheet,
  Key,
  Radio,
  Calendar,
  Camera,
  UserPlus,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

interface CompactLayoutProps {
  children: React.ReactNode;
  currentView: string;
  onViewChange: (view: string) => void;
}

export function CompactLayout({ children, currentView, onViewChange }: CompactLayoutProps) {
  const { profile, signOut, user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
  const [submenuExpanded, setSubmenuExpanded] = useState(true);
  const [companies, setCompanies] = useState<any[]>([]);
  const [enabledIntegrations, setEnabledIntegrations] = useState({
    samsara: false,
    eld: false,
  });
  const [showNotifications, setShowNotifications] = useState(false);
  const [companyLogo, setCompanyLogo] = useState<string | null>(null);
  // Bumped on logoUpdated: a re-upload keeps the same stored string, so the image must remount to re-sign.
  const [logoKey, setLogoKey] = useState(0);
  const [companyName, setCompanyName] = useState<string>('CS NATIONAL');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [pendingAIDecisions, setPendingAIDecisions] = useState(2); // Mock count - in production from API
  const [showReportProblem, setShowReportProblem] = useState(false);
  const [collapsedSubGroups, setCollapsedSubGroups] = useState<Set<string>>(new Set(['drivers-group', 'fleet-group', 'inspections-group', 'reimbursements-group']));
  const [searchLoading, setSearchLoading] = useState(false);

  // Get user display info with fallback
  const userDisplayName = profile?.full_name || user?.email?.split('@')[0] || 'User';
  const userEmail = profile?.email || user?.email || 'user@example.com';
  const userInitials = userDisplayName.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  const [aiEnabled, setAiEnabled] = useState(
    localStorage.getItem('ai_dispatch_enabled') === 'true'
  );
  const [currentTime, setCurrentTime] = useState(new Date());

  // Toggle subgroup collapse state
  const toggleSubGroup = (subGroupId: string) => {
    setCollapsedSubGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(subGroupId)) {
        newSet.delete(subGroupId);
      } else {
        newSet.add(subGroupId);
      }
      return newSet;
    });
  };

  // Load companies for dynamic driver submenu
  const checkEnabledIntegrations = async () => {
    try {
      // Use the status-only edge function (returns booleans, never secrets) so
      // this works for every role after system_settings is locked to admins.
      const { data, error } = await supabase.functions.invoke('integration-status');
      if (error) throw error;

      const status = data?.status || {};
      setEnabledIntegrations({
        samsara: !!(status.samsara?.enabled && status.samsara?.connected),
        eld: !!(status.eld?.enabled && status.eld?.connected),
      });
    } catch (error) {
      console.error('Error checking enabled integrations:', error);
    }
  };

  useEffect(() => {
    loadCompanies();
    checkEnabledIntegrations();
  }, []);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  async function loadCompanies() {
    try {
      const { data: companiesData } = await supabase
        .from('companies')
        .select('id, name, code')
        .eq('is_active', true)
        .order('name');

      if (companiesData) {
        setCompanies(companiesData);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  }

  // Load company logo and name from database
  useEffect(() => {
    loadCompanyData();
    
    // Listen for logo and company data updates
    const handleLogoUpdate = () => {
      setLogoKey(k => k + 1);
      loadCompanyData();
    };
    const handleCompanyUpdate = () => {
      loadCompanyData();
    };
    window.addEventListener('logoUpdated', handleLogoUpdate);
    window.addEventListener('companyUpdated', handleCompanyUpdate);
    return () => {
      window.removeEventListener('logoUpdated', handleLogoUpdate);
      window.removeEventListener('companyUpdated', handleCompanyUpdate);
    };
  }, []);

  async function loadCompanyData() {
    try {
      const { data } = await supabase
        .from('company_settings')
        .select('logo_url, company_name')
        .limit(1)
        .maybeSingle();
      
      if (data?.logo_url) {
        setCompanyLogo(data.logo_url);
      }
      if (data?.company_name) {
        setCompanyName(data.company_name);
      }
    } catch (error) {
      console.error('Error loading company data:', error);
    }
  }

  // Check for pending AI decisions periodically
  useEffect(() => {
    if (aiEnabled) {
      const checkPending = () => {
        // In production, this would fetch from API
        // For now, using mock data
        const pending = 2; // This would come from your backend
        setPendingAIDecisions(pending);
      };
      
      checkPending();
      const interval = setInterval(checkPending, 30000); // Check every 30 seconds
      
      return () => clearInterval(interval);
    }
  }, [aiEnabled]);

  // Listen for AI toggle changes from other components
  useEffect(() => {
    const handleAIToggleChange = (event: CustomEvent) => {
      const enabled = event.detail?.enabled ?? localStorage.getItem('ai_dispatch_enabled') === 'true';
      setAiEnabled(enabled);
    };

    window.addEventListener('aiToggleChanged', handleAIToggleChange as EventListener);
    
    return () => {
      window.removeEventListener('aiToggleChanged', handleAIToggleChange as EventListener);
    };
  }, []);

  const toggleAI = () => {
    const newState = !aiEnabled;
    setAiEnabled(newState);
    localStorage.setItem('ai_dispatch_enabled', String(newState));
    
    // Dispatch event to sync with AI Dispatch page
    window.dispatchEvent(new CustomEvent('aiToggleChanged', { detail: { enabled: newState } }));
  };

  // Advanced search function
  useEffect(() => {
    const searchTimeout = setTimeout(() => {
      if (searchQuery.trim().length >= 2) {
        console.log('Triggering search for:', searchQuery.trim());
        performSearch(searchQuery.trim());
      } else {
        setSearchResults([]);
        setShowSearchResults(false);
      }
    }, 300); // Debounce search

    return () => clearTimeout(searchTimeout);
  }, [searchQuery]);

  async function performSearch(query: string) {
    setSearchLoading(true);
    console.log('🔍 Searching for:', query);
    try {
      const searchTerm = `%${query}%`;
      const results: any[] = [];

      // Search Loads
      const { data: loads, error: loadsError } = await supabase
        .from('loads')
        .select('id, load_number, commodity, reference_number, status')
        .or(`load_number.ilike.${searchTerm},commodity.ilike.${searchTerm},reference_number.ilike.${searchTerm}`)
        .limit(5);
      
      if (loadsError) {
        console.error('Loads search error:', loadsError);
        // Continue with other searches
      }
      console.log('Loads found:', loads?.length || 0);

      if (loads && !loadsError) {
        loads.forEach(load => {
          results.push({
            type: 'load',
            icon: Package,
            title: load.load_number,
            subtitle: `${load.commodity || 'N/A'} • ${load.status}`,
            description: load.reference_number ? `Ref: ${load.reference_number}` : '',
            id: load.id,
            view: 'loads'
          });
        });
      }

      // Search Trucks
      const { data: trucks, error: trucksError } = await supabase
        .from('trucks')
        .select('id, unit_number, make, model, status, vin')
        .or(`unit_number.ilike.${searchTerm},make.ilike.${searchTerm},model.ilike.${searchTerm},vin.ilike.${searchTerm}`)
        .limit(5);
      
      if (trucksError) {
        console.error('Trucks search error:', trucksError);
        // Continue with other searches
      }
      console.log('Trucks found:', trucks?.length || 0);

      if (trucks && !trucksError) {
        trucks.forEach(truck => {
          results.push({
            type: 'truck',
            icon: Truck,
            title: truck.unit_number,
            subtitle: `${truck.make} ${truck.model}`,
            description: `Status: ${truck.status}`,
            id: truck.id,
            view: 'fleet'
          });
        });
      }

      // Search Drivers
      const { data: drivers, error: driversError } = await supabase
        .from('drivers')
        .select('id, first_name, last_name, phone_number, email, cdl_number')
        .or(`first_name.ilike.${searchTerm},last_name.ilike.${searchTerm},phone_number.ilike.${searchTerm},email.ilike.${searchTerm},cdl_number.ilike.${searchTerm}`)
        .limit(5);
      
      if (driversError) {
        console.error('Drivers search error:', driversError);
        // Continue with other searches even if drivers fails
      }
      console.log('Drivers found:', drivers?.length || 0);

      if (drivers && !driversError) {
        drivers.forEach(driver => {
          results.push({
            type: 'driver',
            icon: Users,
            title: `${driver.first_name} ${driver.last_name}`,
            subtitle: driver.phone_number || driver.email || 'No contact',
            description: `CDL: ${driver.cdl_number}`,
            id: driver.id,
            view: 'drivers'
          });
        });
      }

      // Search Dispatches
      const { data: dispatches, error: dispatchesError } = await supabase
        .from('dispatches')
        .select('id, dispatch_number, status, drivers(first_name, last_name), trucks(unit_number)')
        .ilike('dispatch_number', searchTerm)
        .limit(5);
      
      if (dispatchesError) {
        console.error('Dispatches search error:', dispatchesError);
        // Continue with other searches
      }
      console.log('Dispatches found:', dispatches?.length || 0);

      if (dispatches && !dispatchesError) {
        dispatches.forEach(dispatch => {
          const driver = (dispatch as any).drivers;
          const truck = (dispatch as any).trucks;
          results.push({
            type: 'dispatch',
            icon: MapPin,
            title: dispatch.dispatch_number,
            subtitle: driver ? `Driver: ${driver.first_name} ${driver.last_name}` : 'No driver assigned',
            description: `${truck ? `Truck: ${truck.unit_number}` : 'No truck'} • ${dispatch.status}`,
            id: dispatch.id,
            view: 'dispatches'
          });
        });
      }

      // Search Maintenance Logs - First by maintenance type and description
      const { data: maintenance, error: maintenanceError } = await supabase
        .from('maintenance_logs')
        .select('id, truck_id, maintenance_type, description, service_date, trucks(unit_number)')
        .or(`maintenance_type.ilike.${searchTerm},description.ilike.${searchTerm},service_provider.ilike.${searchTerm}`)
        .limit(10);

      if (maintenanceError) {
        console.error('Maintenance search error:', maintenanceError);
        // Continue with other searches
      }
      console.log('Maintenance found (by type/description):', maintenance?.length || 0);

      const maintenanceIds = new Set<string>();
      
      if (maintenance && !maintenanceError) {
        maintenance.forEach(maint => {
          maintenanceIds.add(maint.id);
          results.push({
            type: 'maintenance',
            icon: Wrench,
            title: `Maintenance - ${maint.maintenance_type}`,
            subtitle: `Truck: ${(maint as any).trucks?.unit_number || 'N/A'}`,
            description: maint.service_date ? `Date: ${new Date(maint.service_date).toLocaleDateString()}` : '',
            id: maint.id,
            view: 'maintenance'
          });
        });
      }

      // Also search maintenance by truck unit_number (for searching "2904" to find maintenance for truck 2904)
      const { data: truckMaintenance, error: truckMaintenanceError } = await supabase
        .from('maintenance_logs')
        .select('id, truck_id, maintenance_type, service_date, description, trucks!inner(unit_number)')
        .ilike('trucks.unit_number', searchTerm)
        .limit(10);

      if (truckMaintenanceError) {
        console.error('Truck maintenance search error:', truckMaintenanceError);
        // Continue with other searches
      }
      console.log('Maintenance found (by truck):', truckMaintenance?.length || 0);

      if (truckMaintenance && !truckMaintenanceError) {
        truckMaintenance.forEach(maint => {
          // Avoid duplicates
          if (!maintenanceIds.has(maint.id)) {
            maintenanceIds.add(maint.id);
            results.push({
              type: 'maintenance',
              icon: Wrench,
              title: `Maintenance - ${maint.maintenance_type}`,
              subtitle: `Truck: ${(maint as any).trucks?.unit_number || 'N/A'}`,
              description: maint.service_date ? `Date: ${new Date(maint.service_date).toLocaleDateString()}` : '',
              id: maint.id,
              view: 'maintenance'
            });
          }
        });
      }

      // Search Trailers
      const { data: trailers, error: trailersError } = await supabase
        .from('trailers')
        .select('id, unit_number, trailer_type, status')
        .or(`unit_number.ilike.${searchTerm},trailer_type.ilike.${searchTerm}`)
        .limit(5);

      if (trailersError) {
        console.error('Trailers search error:', trailersError);
        // Continue anyway
      }
      console.log('Trailers found:', trailers?.length || 0);

      if (trailers && !trailersError) {
        trailers.forEach(trailer => {
          results.push({
            type: 'trailer',
            icon: Container,
            title: trailer.unit_number,
            subtitle: trailer.trailer_type,
            description: `Status: ${trailer.status}`,
            id: trailer.id,
            view: 'trailers'
          });
        });
      }

      console.log('📊 Total results found:', results.length);
      console.log('Results breakdown:', results.map(r => ({ type: r.type, title: r.title })));
      
      setSearchResults(results);
      setShowSearchResults(true); // Always show dropdown to display "no results" message
    } catch (error) {
      console.error('❌ Search error:', error);
    } finally {
      setSearchLoading(false);
    }
  }

  const handleSearchResultClick = (result: any) => {
    setSearchQuery('');
    setShowSearchResults(false);
    
    // Navigate to specific detail pages based on result type
    switch (result.type) {
      case 'truck':
        window.history.pushState({}, '', `/fleet/${result.id}`);
        window.dispatchEvent(new PopStateEvent('popstate'));
        break;
      case 'driver':
        window.history.pushState({}, '', `/drivers/${result.id}`);
        window.dispatchEvent(new PopStateEvent('popstate'));
        break;
      case 'load':
        window.history.pushState({}, '', `/loads/${result.id}`);
        window.dispatchEvent(new PopStateEvent('popstate'));
        break;
      case 'maintenance':
        window.history.pushState({}, '', `/maintenance/${result.id}`);
        window.dispatchEvent(new PopStateEvent('popstate'));
        break;
      case 'trailer':
        window.history.pushState({}, '', `/trailers/${result.id}`);
        window.dispatchEvent(new PopStateEvent('popstate'));
        break;
      case 'dispatch':
        window.history.pushState({}, '', `/dispatches/${result.id}`);
        window.dispatchEvent(new PopStateEvent('popstate'));
        break;
      default:
        // For other types, just navigate to the view
        onViewChange(result.view);
        sessionStorage.setItem('selectedItemId', result.id);
        break;
    }
  };

  const handleAIClick = () => {
    if (aiEnabled && pendingAIDecisions > 0) {
      onViewChange('ai-analytics');
    }
  };

  const navigation = [
    { id: 'dashboard', name: 'Home', icon: LayoutDashboard },
    { id: 'samsara-location', name: 'Samsara Location', icon: MapPin },
    { 
      id: 'dispatch-group', 
      name: 'Loads & Dispatching', 
      icon: Package,
      isGroup: true,
      groupKey: 'dispatch',
      children: [
        { id: 'dispatch-board', name: 'Dispatch Board', icon: Calendar },
        { id: 'loads', name: 'Loads', icon: Package },
        { id: 'dispatches', name: 'Dispatches', icon: Truck },
        { id: 'tracking', name: 'Tracking', icon: Navigation },
        { id: 'loads/truck-photos', name: 'Truck Photos', icon: Camera },
        { id: 'loads/trailer-photos', name: 'Trailer Photos', icon: Container },
        { id: 'loads/driver-cdl-twic', name: 'Driver CDL & TWIC', icon: CreditCard },
        { id: 'dispatchers-gross', name: 'Dispatchers Gross', icon: DollarSign, adminOnly: true },
        { id: 'fleet/history', name: 'Assignment History', icon: Clock },
      ]
    },
    { 
      id: 'safety-group', 
      name: 'Safety', 
      icon: Shield,
      isGroup: true,
      groupKey: 'safety',
      children: [
        { id: 'companies', name: 'Manage Companies', icon: Building2 },
        { 
          id: 'drivers-group',
          name: 'Drivers',
          icon: Users,
          isSubGroup: true,
          children: [
            { id: 'drivers', name: 'All Drivers', icon: Users },
            ...companies.map((company: any) => ({
              id: `drivers-${company.code.toLowerCase()}`,
              name: company.name,
              icon: Building2
            }))
          ]
        },
        { 
          id: 'fleet-group',
          name: 'Trucks',
          icon: Truck,
          isSubGroup: true,
          children: [
            { id: 'fleet', name: 'All Trucks', icon: Truck },
            ...companies.map((company: any) => ({
              id: `fleet-${company.code.toLowerCase()}`,
              name: company.name,
              icon: Building2
            }))
          ]
        },
        { id: 'trailers', name: 'Trailers', icon: Container },
        { 
          id: 'inspections-group',
          name: 'Inspections',
          icon: ClipboardCheck,
          isSubGroup: true,
          children: [
            { id: 'inspections', name: 'All Inspections', icon: ClipboardCheck },
            ...companies.map((company: any) => ({
              id: `inspections-${company.code.toLowerCase()}`,
              name: company.name,
              icon: Building2
            }))
          ]
        },
        { id: 'day-off', name: 'Day Off', icon: Calendar },
        { id: 'airplane-tickets-rec', name: 'Airplane Tickets REC', icon: Send },
        { id: 'lift-rec', name: 'Lift REC', icon: Send },
      ]
    },
    { 
      id: 'eld-group', 
      name: 'ELD', 
      icon: Radio,
      isGroup: true,
      groupKey: 'eld',
      children: [
        { id: 'eld-location', name: 'Locations', icon: MapPin },
        { id: 'eld-hours', name: 'Hours of Service', icon: Clock },
      ]
    },
    { 
      id: 'recruiting-group', 
      name: 'Recruiting', 
      icon: UserPlus,
      isGroup: true,
      groupKey: 'recruiting',
      children: [
        { id: 'recruiting-daily', name: 'Daily CRM', icon: Calendar },
        { id: 'recruiting-leads', name: 'Leads', icon: UserPlus },
        { id: 'recruiting-all-drivers', name: 'All Drivers', icon: Users },
        { id: 'recruiting-dolasci-odlasci', name: 'Arrivals/Departures', icon: Calendar },
      ]
    },
    { 
      id: 'analytics', 
      name: 'Analytics', 
      icon: BarChart3,
      adminOnly: true
    },
    { 
      id: 'maintenance-group', 
      name: 'Maintenance', 
      icon: Wrench,
      isGroup: true,
      groupKey: 'maintenance',
      children: [
        { id: 'maintenance', name: 'Maintenance Logs', icon: Wrench },
        { id: 'maintenance-statistics', name: 'Statistics', icon: BarChart3 },
        { id: 'maintenance-key-hider', name: 'Key Hider', icon: Key },
        { id: 'truck-photos', name: 'Truck Photos', icon: Truck },
        { id: 'trailer-photos', name: 'Trailer Photos', icon: Container },
        { id: 'truck-cleaning', name: 'Truck Cleaning', icon: Wrench },
      ]
    },
    { 
      id: 'accounting-group', 
      name: 'Accounting', 
      icon: DollarSign,
      isGroup: true,
      groupKey: 'accounting',
      children: [
        { 
          id: 'reimbursements-group',
          name: 'Expenses',
          icon: Plus,
          isSubGroup: true,
          children: [
            { id: 'reimbursements-hotel', name: 'Hotel', icon: Building2 },
            { id: 'reimbursements-lyft', name: 'Lyft', icon: Navigation },
            { id: 'reimbursements-airplane', name: 'Airplane Tickets', icon: Send },
          ]
        },
        { id: 'deductions', name: 'Deductions', icon: Minus },
        { id: 'deductions-maintenance', name: 'Ded. Maintenance', icon: Minus },
        { id: 'credits', name: 'Credits', icon: Plus },
        { id: 'scheduled-deductions', name: 'Scheduled Deductions', icon: Clock },
        { id: 'fuel', name: 'Fuel', icon: Fuel },
        { id: 'statements', name: 'Statements', icon: FileText },
        { id: 'invoices', name: 'Invoices', icon: Receipt },
        { id: 'tabela', name: 'Tabela', icon: FileText },
      ]
    },
    { 
      id: 'address-book-group', 
      name: 'Address Book', 
      icon: BookOpen,
      isGroup: true,
      groupKey: 'address-book',
      children: [
        { id: 'customers', name: 'Customers', icon: Building2 },
        { id: 'facility-locations', name: 'Facility Locations', icon: MapPin },
        { id: 'factoring-companies', name: 'Factoring Companies', icon: Landmark },
        { id: 'service-providers', name: 'Service Providers', icon: Briefcase },
      ]
    },
    { id: 'documents', name: 'Documents', icon: FileText },
    { id: 'activity', name: 'Activity Log', icon: Activity },
    {
      id: 'company-accounting-group',
      name: 'Company Accounting',
      icon: Building2,
      isGroup: true,
      groupKey: 'company-accounting',
      children: [
        { id: 'company-expenses', name: 'Company Expenses', icon: Receipt },
        { id: 'new-trucks', name: 'New Trucks', icon: Truck },
        { id: 'ifta', name: 'IFTA', icon: FileText },
        { id: 'company-1099', name: '1099', icon: FileText },
      ]
    },
    {
      id: 'employee-payroll-group',
      name: 'Employee Payroll',
      icon: UserCog,
      isGroup: true,
      groupKey: 'employee-payroll',
      children: [
        { id: 'employee-statements', name: 'Statements', icon: FileText },
        { id: 'employee-bonuses', name: 'Bonuses', icon: Plus },
        { id: 'employee-expenses', name: 'Expenses', icon: Minus },
      ]
    },
  ];

  const bottomNav = [
    { id: 'my-tickets', name: 'My Tickets', icon: AlertOctagon },
    { id: 'users', name: 'Users', icon: Users },
    { id: 'settings', name: 'Settings', icon: Settings },
    { 
      id: 'help-group', 
      name: 'Help', 
      icon: HelpCircle,
      isGroup: true,
      groupKey: 'help',
      children: [
        { id: 'documentation', name: 'Documentation', icon: BookOpen },
        { id: 'support-tickets', name: 'Support Tickets', icon: AlertOctagon },
      ]
    },
  ];

  // Filter navigation based on user role
  const getFilteredNavigation = () => {
    const userRole = profile?.role;
    
    // First filter by enabled integrations
    let filteredByIntegrations = navigation.filter(item => {
      // Hide Samsara Location if Samsara not enabled
      if (item.id === 'samsara-location' && !enabledIntegrations.samsara) {
        return false;
      }
      // Hide ELD if ELD not enabled
      if (item.id === 'eld-group' && !enabledIntegrations.eld) {
        return false;
      }
      return true;
    });
    
    // Superadmin sees everything (that's enabled)
    if (userRole === 'superadmin' || userRole === 'Superadmin') {
      return filteredByIntegrations;
    }
    
    // Admin sees everything
    if (userRole === 'admin' || userRole === 'Admin') {
      return filteredByIntegrations;
    }
    
    // Filter out adminOnly items for non-admin users
    const filterAdminOnly = (items: any[]) => {
      return items.filter(item => !(item as any).adminOnly);
    };
    
    // Dispatch role - can see Dashboard (without revenue), Excel, Loads, Dispatches, Tracking, Truck Photos, Address Book, Documents
    if (userRole === 'Dispatch') {
      const filtered = filterAdminOnly(filteredByIntegrations).filter(item => {
        if (item.id === 'dashboard') return true;
        if (item.id === 'dispatch-group') return true;
        if (item.id === 'address-book-group') return true;
        if (item.id === 'documents') return true;
        return false;
      }).map(item => {
        // Filter dispatch-group to show Excel, Loads, Dispatches, Tracking, and Truck Photos
        if (item.id === 'dispatch-group' && item.children) {
          return {
            ...item,
            children: item.children.filter(child => 
              child.id === 'dispatch-board' || child.id === 'loads' || child.id === 'dispatches' || child.id === 'tracking' || child.id === 'loads/truck-photos' || child.id === 'loads/trailer-photos' || child.id === 'loads/driver-cdl-twic'
            )
          };
        }
        return item;
      });
      return filtered;
    }
    
    // Safety role - can see Dashboard, Excel Board (from dispatch-group), Safety group, Expenses (from Accounting)
    if (userRole === 'Safety') {
      return filterAdminOnly(filteredByIntegrations).filter(item => {
        if (item.id === 'dashboard') return true;
        if (item.id === 'dispatch-group') return true;
        if (item.id === 'safety-group') return true;
        if (item.id === 'accounting-group') return true;
        return false;
      }).map(item => {
        // Create a copy to avoid mutating the original navigation
        // Show Dispatch Board and Truck Photos from dispatch-group
        if (item.id === 'dispatch-group' && item.children) {
          return {
            ...item,
            children: item.children.filter(child => child.id === 'dispatch-board' || child.id === 'loads/truck-photos' || child.id === 'loads/trailer-photos' || child.id === 'loads/driver-cdl-twic')
          };
        }
        // Show only Expenses from accounting-group
        if (item.id === 'accounting-group' && item.children) {
          return {
            ...item,
            children: item.children.filter(child => child.id === 'reimbursements-group')
          };
        }
        return item;
      });
    }
    
    // Maintenance role - can see Dashboard, Excel page (view only), and Maintenance tab
    if (userRole === 'Maintenance') {
      return filterAdminOnly(filteredByIntegrations).filter(item => {
        if (item.id === 'dashboard') return true;
        if (item.id === 'dispatch-group') return true;
        if (item.id === 'maintenance-group') return true;
        return false;
      }).map(item => {
        // Only show Excel Board from dispatch-group
        if (item.id === 'dispatch-group' && item.children) {
          return {
            ...item,
            children: item.children.filter(child => child.id === 'dispatch-board')
          };
        }
        return item;
      });
    }
    
    // Dispatch Manager role - same as Dispatch but includes Excel page for driver reassignment
    if (userRole === 'Dispatch Manager') {
      const filtered = filterAdminOnly(filteredByIntegrations).filter(item => {
        if (item.id === 'dashboard') return true;
        if (item.id === 'dispatch-group') return true;
        if (item.id === 'address-book-group') return true;
        if (item.id === 'documents') return true;
        return false;
      }).map(item => {
        // Filter dispatch-group to show Excel, Loads, Dispatches, Tracking, and Truck Photos
        if (item.id === 'dispatch-group' && item.children) {
          return {
            ...item,
            children: item.children.filter(child => 
              child.id === 'dispatch-board' || child.id === 'loads' || child.id === 'dispatches' || child.id === 'tracking' || child.id === 'loads/truck-photos' || child.id === 'loads/trailer-photos' || child.id === 'loads/driver-cdl-twic'
            )
          };
        }
        return item;
      });
      return filtered;
    }
    
    // Safety Manager role - same as Safety role with Expenses access
    if (userRole === 'Safety Manager') {
      return filterAdminOnly(filteredByIntegrations).filter(item => {
        if (item.id === 'dashboard') return true;
        if (item.id === 'dispatch-group') return true;
        if (item.id === 'safety-group') return true;
        if (item.id === 'accounting-group') return true;
        return false;
      }).map(item => {
        // Show Dispatch Board and Truck Photos from dispatch-group
        if (item.id === 'dispatch-group' && item.children) {
          return {
            ...item,
            children: item.children.filter(child => child.id === 'dispatch-board' || child.id === 'loads/truck-photos' || child.id === 'loads/trailer-photos' || child.id === 'loads/driver-cdl-twic')
          };
        }
        // Only show Expenses subgroup from accounting-group with Hotel, Lyft, Airplane, Tickets
        if (item.id === 'accounting-group' && item.children) {
          return {
            ...item,
            children: item.children.filter(child => {
              // Show only the Expenses subgroup
              if (child.id === 'reimbursements-group' && child.children) {
                return true;
              }
              return false;
            }).map(child => {
              // Filter Expenses subgroup to show only Hotel, Lyft, Airplane, Tickets
              if (child.id === 'reimbursements-group' && child.children) {
                return {
                  ...child,
                  children: child.children.filter(expenseChild => 
                    expenseChild.id === 'reimbursements-hotel' || 
                    expenseChild.id === 'reimbursements-lyft' || 
                    expenseChild.id === 'reimbursements-airplane' ||
                    expenseChild.id === 'airplane-tickets-rec'
                  )
                };
              }
              return child;
            })
          };
        }
        return item;
      });
    }
    
    // Accounting Manager role - same permissions as Accounting role
    if (userRole === 'Accounting Manager') {
      return navigation.filter(item => {
        if (item.id === 'dashboard') return true;
        if (item.id === 'dispatch-group') return true;
        if (item.id === 'accounting-group') return true;
        if (item.id === 'address-book-group') return true;
        if (item.id === 'documents') return true;
        if (item.id === 'analytics') return true;
        return false;
      }).map(item => {
        // Show Excel, Loads, Dispatches and Dispatchers Gross from dispatch-group
        if (item.id === 'dispatch-group' && item.children) {
          return {
            ...item,
            children: item.children.filter(child =>
              child.id === 'dispatch-board' || child.id === 'loads' || child.id === 'dispatches' || child.id === 'dispatchers-gross'
            )
          };
        }
        return item;
      });
    }
    
    // Accounting role - can see Dashboard, Excel page (view only), Loads, Dispatches, Accounting, Documents, Address Book
    if (userRole === 'Accounting') {
      return navigation.filter(item => {
        if (item.id === 'dashboard') return true;
        if (item.id === 'dispatch-group') return true;
        if (item.id === 'accounting-group') return true;
        if (item.id === 'address-book-group') return true;
        if (item.id === 'documents') return true;
        return false;
      }).map(item => {
        // Show Excel, Loads, Dispatches and Dispatchers Gross from dispatch-group
        if (item.id === 'dispatch-group' && item.children) {
          return {
            ...item,
            children: item.children.filter(child =>
              child.id === 'dispatch-board' || child.id === 'loads' || child.id === 'dispatches' || child.id === 'dispatchers-gross'
            )
          };
        }
        return item;
      });
    }
    
    // Afterhours role - can see Dashboard, Loads & Dispatching (full access), Documents
    if (userRole === 'Afterhours') {
      return navigation.filter(item => {
        if (item.id === 'dashboard') return true;
        if (item.id === 'dispatch-group') return true; // Full Loads & Dispatching group
        if (item.id === 'documents') return true;
        return false;
      }).map(item => {
        if (item.id === 'dispatch-group' && item.children) {
          return {
            ...item,
            children: item.children.filter(child => !(child as any).adminOnly)
          };
        }
        return item;
      });
    }
    
    // ELD role - can see Dashboard, Excel page (view only), and ELD tab
    if (userRole === 'ELD') {
      return navigation.filter(item => {
        if (item.id === 'dashboard') return true;
        if (item.id === 'dispatch-group') return true;
        if (item.id === 'eld-group') return true;
        return false;
      }).map(item => {
        // Only show Excel Board from dispatch-group
        if (item.id === 'dispatch-group' && item.children) {
          return {
            ...item,
            children: item.children.filter(child => child.id === 'dispatch-board')
          };
        }
        return item;
      });
    }
    
    // Recruiting role - can see Recruiting tab and Airplane Tickets from Accounting
    if (userRole === 'Recruiting') {
      return filterAdminOnly(filteredByIntegrations).filter(item => {
        if (item.id === 'dashboard') return true;
        if (item.id === 'recruiting-group') return true;
        if (item.id === 'accounting-group') return true;
        return false;
      }).map(item => {
        // Only show Airplane Tickets from accounting-group
        if (item.id === 'accounting-group' && item.children) {
          return {
            ...item,
            children: item.children.filter(child => {
              // Show only the Expenses subgroup
              if (child.id === 'reimbursements-group' && child.children) {
                return true;
              }
              return false;
            }).map(child => {
              // Filter Expenses subgroup to show only Airplane Tickets
              if (child.id === 'reimbursements-group' && child.children) {
                return {
                  ...child,
                  children: child.children.filter(subChild => subChild.id === 'reimbursements-airplane')
                };
              }
              return child;
            })
          };
        }
        return item;
      });
    }
    
    // Default: show only dashboard
    return navigation.filter(item => item.id === 'dashboard');
  };

  // Filter bottom navigation based on role
  const getFilteredBottomNav = () => {
    const userRole = profile?.role;
    
    // Superadmin sees Unlock Accounts
    if (userRole === 'Superadmin' || userRole === 'superadmin') {
      return [
        { id: 'users', name: 'Users', icon: Users },
        { id: 'unlock-accounts', name: 'Unlock Accounts', icon: Shield },
        { id: 'settings', name: 'Settings', icon: Settings },
        { 
          id: 'help-group', 
          name: 'Help', 
          icon: HelpCircle,
          isGroup: true,
          groupKey: 'help',
          children: [
            { id: 'documentation', name: 'Documentation', icon: BookOpen },
            { id: 'support-tickets', name: 'Support Tickets', icon: AlertOctagon },
          ]
        },
      ];
    }
    
    // Admin can see Users but not Support Tickets or Unlock Accounts
    if (userRole === 'Admin') {
      return bottomNav.filter(item => item.id !== 'my-tickets');
    }
    
    // Other roles can see My Tickets, Settings, and Help (but not Users)
    return bottomNav.filter(item => item.id !== 'users');
  };

  const filteredNavigation = getFilteredNavigation();
  const filteredBottomNav = getFilteredBottomNav();

  return (
    <div className="h-screen flex flex-col bg-slate-50">
      <header className="bg-gradient-to-r from-indigo-900 via-blue-900 to-indigo-900 border-b border-blue-700/50 px-2 sm:px-4 py-2 sm:py-3 flex items-center justify-between gap-2 sm:gap-4 z-10 shadow-lg">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="bg-white p-1.5 sm:p-2 rounded-lg">
            <SignedImage
              key={logoKey}
              stored={companyLogo}
              alt="CS NATIONAL Logo"
              className="w-4 h-4 sm:w-5 sm:h-5 object-contain"
              fallback={<img src="/logo.png" alt="CS NATIONAL Logo" className="w-4 h-4 sm:w-5 sm:h-5 object-contain" />}
            />
          </div>
          <div className="flex flex-col">
            <span className="text-[10px] sm:text-xs text-blue-300 uppercase">Company</span>
            <span className="text-xs sm:text-sm font-bold text-white truncate max-w-[120px] sm:max-w-none">{companyName}</span>
          </div>
        </div>

        <div className="hidden md:flex flex-1 max-w-2xl relative mx-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-300/70 w-5 h-5" />
          <input
            type="text"
            placeholder="Search for Loads / Trucks / Drivers / Equipment and More..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => searchQuery.trim().length >= 2 && setShowSearchResults(true)}
            onBlur={() => setTimeout(() => setShowSearchResults(false), 200)}
            className="w-full pl-10 pr-4 py-2 bg-blue-950/50 border border-blue-700/50 text-white placeholder-blue-300/50 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-sm backdrop-blur-sm"
          />
          {searchLoading && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
            </div>
          )}
          
          {/* Search Results Dropdown */}
          {showSearchResults && (
            <div className="absolute top-full mt-2 w-full bg-blue-950 border border-blue-700/50 rounded-lg shadow-xl z-[99999] max-h-[500px] overflow-y-auto backdrop-blur-sm">
              <div className="p-2">
                {searchResults.length > 0 ? (
                  <>
                    <div className="text-xs text-blue-300 px-3 py-2 font-semibold">
                      Found {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
                    </div>
                    {searchResults.map((result, index) => {
                      const Icon = result.icon;
                      return (
                        <button
                          key={`${result.type}-${result.id}-${index}`}
                          onClick={() => handleSearchResultClick(result)}
                          className="w-full flex items-start gap-3 p-3 hover:bg-blue-900/50 rounded-lg transition-colors text-left"
                        >
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            result.type === 'load' ? 'bg-blue-600' :
                            result.type === 'truck' ? 'bg-red-600' :
                            result.type === 'driver' ? 'bg-purple-600' :
                            result.type === 'dispatch' ? 'bg-green-600' :
                            result.type === 'maintenance' ? 'bg-orange-600' :
                            result.type === 'trailer' ? 'bg-indigo-600' :
                            'bg-slate-600'
                          }`}>
                            <Icon className="w-5 h-5 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-semibold text-white truncate">{result.title}</p>
                              <span className="text-xs px-2 py-0.5 rounded-full bg-blue-800/50 text-blue-200 capitalize flex-shrink-0">
                                {result.type}
                              </span>
                            </div>
                            <p className="text-xs text-blue-300 truncate mt-0.5">{result.subtitle}</p>
                            {result.description && (
                              <p className="text-xs text-blue-400 truncate mt-0.5">{result.description}</p>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </>
                ) : (
                  <div className="px-3 py-8 text-center">
                    <Search className="w-12 h-12 text-blue-700 mx-auto mb-3" />
                    <p className="text-sm text-blue-300 font-medium">No results found</p>
                    <p className="text-xs text-blue-400 mt-1">Try searching for a different term</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 sm:gap-3 ml-auto">
          {/* Chicago Date & Time - Hidden on mobile */}
          <div className="hidden lg:flex items-center gap-2 px-3 py-2 bg-blue-950/50 rounded-lg border border-blue-700/50 backdrop-blur-sm">
            <Calendar className="w-4 h-4 text-blue-300" />
            <div className="flex flex-col">
              <span className="text-xs font-medium text-blue-100">
                {currentTime.toLocaleDateString('en-US', { 
                  timeZone: 'America/Chicago',
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </span>
              <span className="text-xs text-blue-300">
                {currentTime.toLocaleTimeString('en-US', { 
                  timeZone: 'America/Chicago',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit'
                })} CST
              </span>
            </div>
          </div>

          {/* Supabase Status */}
          <SupabaseStatus />
          
          {/* Real-time Notifications */}
          <NotificationDropdown />
          <div className="relative flex items-center gap-2 pl-2 sm:pl-3 border-l border-blue-700/50">
            <button
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              className="w-8 h-8 rounded-full bg-[#AC021A] text-white flex items-center justify-center font-semibold text-sm hover:bg-[#8A0115] transition-colors"
            >
              {userInitials}
            </button>
            
            {/* Profile Dropdown */}
            {showProfileMenu && (
              <div className="absolute right-0 top-full mt-2 w-64 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-50">
                <div className="p-4 border-b border-slate-700">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-[#AC021A] text-white flex items-center justify-center font-semibold text-lg">
                      {userInitials}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-white">{userDisplayName}</p>
                      <p className="text-xs text-slate-400">{userEmail}</p>
                    </div>
                  </div>
                </div>
                <div className="p-2">
                  <button
                    onClick={() => {
                      onViewChange('settings');
                      setShowProfileMenu(false);
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2 text-slate-300 hover:bg-slate-700 hover:text-white rounded-lg transition-colors"
                  >
                    <Settings className="w-4 h-4" />
                    <span className="text-sm">Settings</span>
                  </button>
                  <button
                    onClick={() => {
                      signOut();
                      setShowProfileMenu(false);
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2 text-red-400 hover:bg-red-600 hover:text-white rounded-lg transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    <span className="text-sm">Logout</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        <aside className="w-16 bg-gradient-to-b from-indigo-900 via-blue-900 to-indigo-900 flex flex-col justify-between relative z-20 shadow-lg">
          <div>

            <nav className="flex flex-col p-1 space-y-0.5">
              {filteredNavigation.map((item: any) => {
                const Icon = item.icon;
                
                // Handle grouped items
                if (item.isGroup) {
                  // Check if any child or nested subchild is active
                  const hasActiveChild = item.children?.some((child: any) => {
                    if (child.isSubGroup) {
                      return child.children?.some((subChild: any) => currentView === subChild.id);
                    }
                    return currentView === child.id;
                  });
                  
                  return (
                    <button
                      key={item.id}
                      onClick={() => {
                        setActiveSubmenu(activeSubmenu === item.groupKey ? null : item.groupKey);
                        
                        // Auto-navigate to first child for recruiting group
                        if (item.groupKey === 'recruiting' && item.children && item.children.length > 0) {
                          const firstChild = item.children[0];
                          window.history.pushState({}, '', `/${firstChild.id}`);
                          window.dispatchEvent(new Event('urlchange'));
                          onViewChange(firstChild.id);
                        }
                      }}
                      className={`flex flex-col items-center justify-center p-1 rounded-lg transition-all ${
                        hasActiveChild || activeSubmenu === item.groupKey
                          ? 'bg-blue-800/50 text-white backdrop-blur-sm'
                          : 'text-blue-300 hover:bg-blue-800/50 hover:text-white'
                      }`}
                    >
                      <Icon className="w-5 h-5 mb-1" />
                      <span className="text-[9px] text-center leading-tight">{item.name}</span>
                    </button>
                  );
                }
                
                // Handle regular items
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      window.history.pushState({}, '', `/${item.id}`);
                      window.dispatchEvent(new Event('urlchange'));
                      onViewChange(item.id);
                      setActiveSubmenu(null); // Close any open submenu when clicking non-grouped item
                    }}
                    className={`flex flex-col items-center justify-center p-1.5 rounded-lg transition-all ${
                      currentView === item.id
                        ? 'bg-blue-800/50 text-white backdrop-blur-sm'
                        : 'text-blue-300 hover:bg-blue-800/50 hover:text-white'
                    }`}
                  >
                    <Icon className="w-5 h-5 mb-1" />
                    <span className="text-[9px] text-center leading-tight">{item.name}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="flex flex-col p-1 space-y-0.5 border-t border-blue-700/50">
            {filteredBottomNav.map((item: any) => {
              const Icon = item.icon;
              
              // Handle grouped items
              if (item.isGroup) {
                // Check if any child is active
                const hasActiveChild = item.children?.some((child: any) => currentView === child.id);
                
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      setActiveSubmenu(activeSubmenu === item.groupKey ? null : item.groupKey);
                    }}
                    className={`flex flex-col items-center justify-center p-1 rounded-lg transition-all ${
                      hasActiveChild || activeSubmenu === item.groupKey
                        ? 'bg-blue-800/50 text-white backdrop-blur-sm'
                        : 'text-blue-300 hover:bg-blue-800/50 hover:text-white'
                    }`}
                  >
                    <Icon className="w-4 h-4 mb-0.5" />
                    <span className="text-[8px] text-center leading-tight">{item.name}</span>
                  </button>
                );
              }
              
              // Handle regular items
              const isActive = currentView === item.id;

              return (
                <button
                  key={item.id}
                  onClick={() => {
                    if (item.action) {
                      item.action();
                    } else {
                      window.history.pushState({}, '', `/${item.id}`);
                      window.dispatchEvent(new Event('urlchange'));
                      onViewChange(item.id);
                      setActiveSubmenu(null); // Close any open submenu
                    }
                  }}
                  className={`flex flex-col items-center justify-center p-1 rounded-lg transition-all ${
                    isActive
                      ? 'bg-blue-800/50 text-white backdrop-blur-sm'
                      : 'text-blue-300 hover:bg-blue-800/50 hover:text-white'
                  }`}
                >
                  <Icon className="w-4 h-4 mb-0.5" />
                  <span className="text-[8px] text-center leading-tight">{item.name}</span>
                </button>
              );
            })}

            <button
              onClick={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('Logout button clicked');
                
                // Set a timeout to force redirect if signOut hangs
                const forceLogoutTimeout = setTimeout(() => {
                  console.log('SignOut timeout - forcing logout');
                  localStorage.clear();
                  sessionStorage.clear();
                  window.location.href = '/';
                }, 2000); // 2 second timeout
                
                try {
                  console.log('Calling signOut...');
                  await signOut();
                  console.log('SignOut successful');
                  clearTimeout(forceLogoutTimeout);
                } catch (error) {
                  console.error('Logout error:', error);
                  clearTimeout(forceLogoutTimeout);
                  // If logout fails, clear local storage manually
                  localStorage.clear();
                  sessionStorage.clear();
                } finally {
                  // Always redirect to home
                  console.log('Redirecting to home...');
                  window.location.href = '/';
                }
              }}
              className="flex flex-col items-center justify-center p-1 rounded-lg text-blue-300 hover:bg-red-600 hover:text-white transition-all cursor-pointer"
              type="button"
            >
              <LogOut className="w-4 h-4 mb-0.5" />
              <span className="text-[8px] text-center leading-tight">Logout</span>
            </button>
          </div>
        </aside>

        {/* Second-level sidebar for menu groups */}
        {activeSubmenu && (
          <aside className={`${submenuExpanded ? 'w-64' : 'w-16'} bg-blue-950/90 border-r border-blue-700/50 flex flex-col z-0 transition-all duration-300 backdrop-blur-sm`}>
            <div className="p-4 border-b border-blue-700/50">
              <div className="flex items-center justify-between">
                {submenuExpanded && (
                  <h3 className="text-sm font-semibold text-white">
                    {[...filteredNavigation, ...filteredBottomNav].find((item: any) => item.groupKey === activeSubmenu)?.name}
                  </h3>
                )}
                <button
                  onClick={() => setSubmenuExpanded(!submenuExpanded)}
                  className="p-1 hover:bg-slate-700 rounded transition-colors ml-auto"
                  title={submenuExpanded ? 'Collapse' : 'Expand'}
                >
                  {submenuExpanded ? (
                    <ChevronLeft className="w-4 h-4 text-slate-400" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  )}
                </button>
              </div>
            </div>
            <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
              {[...filteredNavigation, ...filteredBottomNav]
                .find((item: any) => item.groupKey === activeSubmenu)
                ?.children?.map((child: any) => {
                  const ChildIcon = child.icon;
                  const isActive = currentView === child.id;
                  
                  // Handle subgroups (nested menus)
                  if (child.isSubGroup) {
                    const isSubGroupCollapsed = collapsedSubGroups.has(child.id);
                    const hasActiveSubChild = child.children?.some((subChild: any) => currentView === subChild.id);
                    
                    // When collapsed, show as a clickable button that navigates to first child
                    if (!submenuExpanded) {
                      return (
                        <button
                          key={child.id}
                          onClick={() => {
                            // Navigate to first child
                            const firstChild = child.children?.[0];
                            if (firstChild) {
                              window.history.pushState({}, '', `/${firstChild.id}`);
                              window.dispatchEvent(new Event('urlchange'));
                              onViewChange(firstChild.id);
                            }
                          }}
                          className={`w-full flex flex-col items-center justify-center p-2 rounded-lg transition-colors ${
                            hasActiveSubChild
                              ? 'bg-[#1A143A] text-white'
                              : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                          }`}
                          title={child.name}
                        >
                          <ChildIcon className="w-4 h-4 flex-shrink-0 mb-1" />
                          <span className="text-[9px] text-center leading-tight">{child.name}</span>
                        </button>
                      );
                    }
                    
                    // When expanded, show as a collapsible header with children
                    return (
                      <div key={child.id} className="space-y-1">
                        <button
                          onClick={() => toggleSubGroup(child.id)}
                          className="w-full px-3 py-2 text-xs font-semibold text-slate-400 uppercase flex items-center gap-2 hover:text-slate-300 hover:bg-slate-700 rounded-lg transition-colors"
                        >
                          <ChildIcon className="w-3 h-3" />
                          <span className="flex-1 text-left">{child.name}</span>
                          {isSubGroupCollapsed ? (
                            <ChevronDown className="w-3 h-3" />
                          ) : (
                            <ChevronUp className="w-3 h-3" />
                          )}
                        </button>
                        {!isSubGroupCollapsed && child.children?.map((subChild: any) => {
                          const SubChildIcon = subChild.icon;
                          const isSubActive = currentView === subChild.id;
                          
                          // Handle nested subgroups (level 4)
                          if (subChild.isSubGroup) {
                            const isNestedCollapsed = collapsedSubGroups.has(subChild.id);
                            const hasActiveNestedChild = subChild.children?.some((nestedChild: any) => currentView === nestedChild.id);
                            
                            return (
                              <div key={subChild.id} className="space-y-1">
                                <button
                                  onClick={() => toggleSubGroup(subChild.id)}
                                  className="w-full px-3 py-2 text-xs font-semibold text-slate-400 flex items-center gap-2 hover:text-slate-300 hover:bg-slate-700 rounded-lg transition-colors pl-6"
                                >
                                  <SubChildIcon className="w-3 h-3" />
                                  <span className="flex-1 text-left">{subChild.name}</span>
                                  {isNestedCollapsed ? (
                                    <ChevronDown className="w-3 h-3" />
                                  ) : (
                                    <ChevronUp className="w-3 h-3" />
                                  )}
                                </button>
                                {!isNestedCollapsed && subChild.children?.map((nestedChild: any) => {
                                  const NestedIcon = nestedChild.icon;
                                  const isNestedActive = currentView === nestedChild.id;
                                  return (
                                    <button
                                      key={nestedChild.id}
                                      onClick={() => {
                                        window.history.pushState({}, '', `/${nestedChild.id}`);
                                        window.dispatchEvent(new Event('urlchange'));
                                        onViewChange(nestedChild.id);
                                      }}
                                      className={`w-full flex items-center gap-3 pl-9 pr-3 py-2 rounded-lg transition-colors ${
                                        isNestedActive
                                          ? 'bg-[#1A143A] text-white'
                                          : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                                      }`}
                                    >
                                      <NestedIcon className="w-4 h-4 flex-shrink-0" />
                                      <span className="text-sm">{nestedChild.name}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            );
                          }
                          
                          // Regular subchild item
                          return (
                            <button
                              key={subChild.id}
                              onClick={() => {
                                window.history.pushState({}, '', `/${subChild.id}`);
                                window.dispatchEvent(new Event('urlchange'));
                                onViewChange(subChild.id);
                              }}
                              className={`w-full flex items-center gap-3 pl-6 pr-3 py-2 rounded-lg transition-colors ${
                                isSubActive
                                  ? 'bg-[#1A143A] text-white'
                                  : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                              }`}
                            >
                              <SubChildIcon className="w-4 h-4 flex-shrink-0" />
                              <span className="text-sm">{subChild.name}</span>
                            </button>
                          );
                        })}
                      </div>
                    );
                  }
                  
                  // Regular menu item
                  return (
                    <button
                      key={child.id}
                      onClick={() => {
                        window.history.pushState({}, '', `/${child.id}`);
                        window.dispatchEvent(new Event('urlchange'));
                        onViewChange(child.id);
                      }}
                      className={`w-full flex ${submenuExpanded ? 'items-center gap-3 px-3 py-2.5' : 'flex-col items-center justify-center p-2'} rounded-lg transition-colors ${
                        isActive
                          ? 'bg-[#1A143A] text-white'
                          : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                      }`}
                      title={!submenuExpanded ? child.name : undefined}
                    >
                      <ChildIcon className={`w-4 h-4 flex-shrink-0 ${!submenuExpanded ? 'mb-1' : ''}`} />
                      {submenuExpanded ? (
                        <span className="text-sm font-medium">{child.name}</span>
                      ) : (
                        <span className="text-[9px] text-center leading-tight">{child.name}</span>
                      )}
                    </button>
                  );
                })}
            </nav>
          </aside>
        )}

        <main className="flex-1 overflow-auto bg-slate-50 p-2">
          {children}
        </main>
      </div>

      {/* Report Problem Modal */}
      {showReportProblem && (
        <ReportProblemModal onClose={() => setShowReportProblem(false)} />
      )}
    </div>
  );
}
